import { Button, Checkbox, cn, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@1trading/react';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type Row,
  type RowData,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronsUpDown } from 'lucide-react';
import { type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Rendered in the header cell next to the title/sort control (e.g. a column filter popover). */
    filterControl?: ReactNode;
  }
}

const DEFAULT_LOADING_ROWS = 5;
const SELECTION_COLUMN_ID = 'select';

// Stable fallback: an inline `data ?? []` reads as a data change every render, which
// TanStack's auto-reset turns into an infinite render loop.
const EMPTY_DATA: never[] = [];

// NUL-joined so ids containing separators can't collide.
export const computeSelectionSignature = (ids: string[]): string => [...ids].sort().join('\u0000');

export interface SelectionEmission<TData> {
  signature: string;
  rows: TData[];
}

// Compares rows, not the `data` reference, so an unstable `data` identity can't drive
// an emit → setState → re-render loop. A refetch swaps row objects and must still emit.
export const shouldEmitSelection = <TData,>(last: SelectionEmission<TData> | null, next: SelectionEmission<TData>): boolean => {
  if (!last || last.signature !== next.signature) return true;
  if (last.rows.length !== next.rows.length) return true;
  return last.rows.some((row, index) => row !== next.rows[index]);
};

export interface DataTableProps<TData> {
  // biome-ignore lint/suspicious/noExplicitAny: TanStack column defs are heterogeneous by design
  columns: ColumnDef<TData, any>[];
  data: TData[] | undefined;
  isLoading?: boolean;
  emptyMessage?: string;
  /** Enables client-side pagination with this page size */
  pageSize?: number;
  /** Initial sort order (header controls still override it). Read once on mount. */
  defaultSorting?: SortingState;
  onRowClick?: (row: TData) => void;
  rowClassName?: (row: TData) => string | undefined;
  /** A ticked row stays untickable even when the per-row function rejects it */
  enableRowSelection?: boolean | ((row: TData) => boolean);
  /** Header checkbox ticking every row across all pages */
  enableSelectAll?: boolean;
  /** Controlled selection keyed by `getRowId`, paired with `onRowSelectionChange`; internal state when omitted */
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  /** Called with the selected rows' `original` objects. Read through a ref, so an inline closure is safe. */
  onSelectionChange?: (rows: TData[]) => void;
  getRowId?: (row: TData, index: number) => string;
  /** Adds `bg-card` so the table reads as a lifted surface. Set `false` when a `Card` ancestor already paints one. */
  surface?: boolean;
  /**
   * Ratchet each column's width to the widest it has rendered, so filtering rows
   * out never shrinks the table underneath an open filter popover. Columns can
   * still grow; the ratchet resets when the column set changes.
   */
  stableColumnWidths?: boolean;
  /** Caps the table at most of the viewport and pins the header row while its rows scroll beneath it. */
  stickyHeader?: boolean;
}

export const DataTable = <TData,>({
  columns,
  data,
  isLoading = false,
  emptyMessage = 'No results',
  pageSize,
  defaultSorting,
  onRowClick,
  rowClassName,
  enableRowSelection = false,
  enableSelectAll = true,
  rowSelection: controlledRowSelection,
  onRowSelectionChange,
  onSelectionChange,
  getRowId,
  surface = true,
  stableColumnWidths = false,
  stickyHeader = false,
}: DataTableProps<TData>) => {
  const [sorting, setSorting] = useState<SortingState>(defaultSorting ?? []);
  const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({});

  // Latest-callback ref: keyed on identity, an inline callback would refire the effect every render.
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  const lastEmissionRef = useRef<SelectionEmission<TData> | null>(null);

  const rowSelection = controlledRowSelection ?? internalRowSelection;
  const hasSelectionColumn = enableRowSelection !== false;
  const canSelectRow = typeof enableRowSelection === 'function' ? (row: Row<TData>) => enableRowSelection(row.original) : enableRowSelection;

  const allColumns = useMemo(() => {
    if (!hasSelectionColumn) return columns;
    // biome-ignore lint/suspicious/noExplicitAny: see columns prop
    const selectionColumn: ColumnDef<TData, any> = {
      id: SELECTION_COLUMN_ID,
      enableSorting: false,
      header: enableSelectAll
        ? ({ table }) => (
            <Checkbox
              aria-label="Select all rows"
              checked={table.getIsAllRowsSelected() || (table.getIsSomeRowsSelected() && 'indeterminate')}
              onCheckedChange={(checked) => table.toggleAllRowsSelected(checked === true)}
            />
          )
        : undefined,
      cell: ({ row }) => (
        <Checkbox
          aria-label="Select row"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect() && !row.getIsSelected()}
          onCheckedChange={(checked) => row.toggleSelected(checked === true)}
          onClick={(event) => event.stopPropagation()}
        />
      ),
    };
    return [selectionColumn, ...columns];
  }, [columns, hasSelectionColumn, enableSelectAll]);

  const table = useReactTable({
    data: data ?? EMPTY_DATA,
    columns: allColumns,
    state: { sorting, rowSelection },
    onSortingChange: setSorting,
    onRowSelectionChange: onRowSelectionChange ?? setInternalRowSelection,
    enableRowSelection: canSelectRow,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(pageSize ? { getPaginationRowModel: getPaginationRowModel(), initialState: { pagination: { pageSize } } } : {}),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: `table` is a stable ref, so the effect keys on the state read through it.
  useEffect(() => {
    const selectedRows = table.getSelectedRowModel().rows;
    const emission: SelectionEmission<TData> = {
      signature: computeSelectionSignature(selectedRows.map((row) => row.id)),
      rows: selectedRows.map((row) => row.original),
    };
    if (!shouldEmitSelection(lastEmissionRef.current, emission)) return;
    lastEmissionRef.current = emission;
    onSelectionChangeRef.current?.(emission.rows);
  }, [table, rowSelection, data]);

  const columnCount = allColumns.length;
  const rows = table.getRowModel().rows;

  // Width ratchet: styles are written straight to the DOM (never via state) so
  // re-measuring can't trigger a render loop. Re-applied every commit because
  // React recreates the <th> elements without the inline min-width.
  const headerRowRef = useRef<HTMLTableRowElement>(null);
  const ratchetRef = useRef<{ signature: string; widths: Map<string, number> }>({ signature: '', widths: new Map() });
  useLayoutEffect(() => {
    if (!stableColumnWidths) return;
    const headerRow = headerRowRef.current;
    if (!headerRow) return;
    const cells = Array.from(headerRow.cells);
    const signature = cells.map((cell) => cell.dataset.columnId ?? '').join('\0');
    if (signature !== ratchetRef.current.signature) {
      ratchetRef.current = { signature, widths: new Map() };
    }
    const { widths } = ratchetRef.current;
    for (const cell of cells) {
      const columnId = cell.dataset.columnId;
      if (!columnId) continue;
      const measured = Math.ceil(cell.getBoundingClientRect().width);
      const width = Math.max(widths.get(columnId) ?? 0, measured);
      widths.set(columnId, width);
      cell.style.minWidth = `${width}px`;
    }
  });

  return (
    <div className="flex flex-col gap-3">
      {/* Sticky needs the library Table's own overflow wrapper (the direct child div) to be the bounded scroller. */}
      <div className={cn('rounded-md border', surface && 'bg-card', stickyHeader && '[&>div]:max-h-[75dvh]')}>
        {/* w-max keeps natural column widths; min-w-full stretches when narrow. */}
        <Table className="w-max min-w-full">
          {/* Collapsed table borders don't travel with a sticky row, so the divider is an inset shadow instead. */}
          <TableHeader className={cn(stickyHeader && 'sticky top-0 z-10 bg-card [&_th]:shadow-[inset_0_-1px_0_hsl(var(--border))]')}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} ref={headerRowRef}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sortDirection = header.column.getIsSorted();
                  const filterControl = header.column.columnDef.meta?.filterControl;
                  const headerContent = header.isPlaceholder ? null : canSort ? (
                    <button className="inline-flex items-center gap-1 hover:text-foreground" onClick={header.column.getToggleSortingHandler()} type="button">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {sortDirection === 'asc' ? (
                        <ArrowUp className="size-3.5" />
                      ) : sortDirection === 'desc' ? (
                        <ArrowDown className="size-3.5" />
                      ) : (
                        <ChevronsUpDown className="size-3.5 opacity-50" />
                      )}
                    </button>
                  ) : (
                    flexRender(header.column.columnDef.header, header.getContext())
                  );
                  return (
                    <TableHead data-column-id={header.id} key={header.id}>
                      {filterControl ? (
                        <div className="flex items-center gap-0.5">
                          {headerContent}
                          {filterControl}
                        </div>
                      ) : (
                        headerContent
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from({ length: pageSize ?? DEFAULT_LOADING_ROWS }, (_, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows
                  <TableRow key={index}>
                    {allColumns.map((column, cellIndex) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton cells
                      <TableCell key={cellIndex}>
                        <Skeleton className={column.id === SELECTION_COLUMN_ID ? 'size-4 rounded-sm' : 'h-4 w-full max-w-32'} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : null}
            {!isLoading && rows.length === 0 ? (
              <TableRow>
                <TableCell className="h-24 text-center text-muted-foreground" colSpan={columnCount}>
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : null}
            {!isLoading
              ? rows.map((row) => (
                  <TableRow
                    className={cn(onRowClick && 'cursor-pointer', rowClassName?.(row.original))}
                    data-state={row.getIsSelected() ? 'selected' : undefined}
                    key={row.id}
                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              : null}
          </TableBody>
        </Table>
      </div>
      {pageSize && table.getPageCount() > 1 ? (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {table.getFilteredRowModel().rows.length} rows · page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </span>
          <div className="flex items-center gap-1">
            <Button disabled={!table.getCanPreviousPage()} onClick={() => table.setPageIndex(0)} size="icon" variant="outline">
              <ChevronsLeft className="size-4" />
            </Button>
            <Button disabled={!table.getCanPreviousPage()} onClick={() => table.previousPage()} size="icon" variant="outline">
              <ChevronLeft className="size-4" />
            </Button>
            <Button disabled={!table.getCanNextPage()} onClick={() => table.nextPage()} size="icon" variant="outline">
              <ChevronRight className="size-4" />
            </Button>
            <Button disabled={!table.getCanNextPage()} onClick={() => table.setPageIndex(table.getPageCount() - 1)} size="icon" variant="outline">
              <ChevronsRight className="size-4" />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
DataTable.displayName = 'DataTable';
